import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimWoo, Wordmark } from '@cowoo/shared/ui-web';

/**
 * The frame every backoffice auth route shares (10-web-admin § 2.1,
 * artboards `AdminLogin` / `AdminLoginCode`).
 *
 * Same two-column layout as the member login of `09` § 4, because it is the
 * same flow — but with the brand panel instead of the marketing photo. A
 * backoffice does not sell anything, and the photo carries copy about booking a
 * desk that has nothing to do with signing in to run the place.
 *
 * The "Admin" badge is the one thing that tells these pages apart from the
 * member app's, which is the point: an operator who lands on the wrong host
 * should notice before typing a code into it.
 */
export function AuthLayout({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children?: ReactNode;
}) {
  return (
    <main className="grid min-h-screen lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center px-5 py-12 lg:p-12">
        <div className="flex w-full max-w-[420px] flex-col gap-3.5">
          <h1 className={`text-webH2 text-ink ${body ? '' : 'pb-3'}`}>{title}</h1>
          {body ? <p className="text-webSmall text-muted pb-3">{body}</p> : null}
          {children}
        </div>
      </div>
    </main>
  );
}

function BrandPanel() {
  const { t } = useTranslation();
  return (
    // The gradient sits on top of a solid `teal-dark`: a panel whose colour
    // lives only in `background-image` reads as "no background" to anything
    // measuring it, the design check included.
    <aside className="bg-teal-dark from-teal to-teal-dark relative hidden overflow-hidden bg-gradient-to-br lg:block">
      <div className="relative flex h-full flex-col justify-between px-14 py-12 text-white">
        <div className="flex items-center gap-3">
          <Wordmark size={40} />
          <span className="font-body rounded-pill bg-white/[0.18] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-white">
            {t('admin.badge')}
          </span>
        </div>
        <p className="text-webH1">
          <ClaimWoo text={t('member.login.panelClaim')} />
        </p>
      </div>
    </aside>
  );
}
